import type { ReactNode } from "react";
import { ChevronRight } from "lucide-react";

interface BoardGroupProps {
  eyebrow: string;
  title: string;
  subtitle?: string | null;
  total: number;
  done: number;
  blocking?: number;
  /** A CSS color used for the left accent bar + progress fill. */
  accent?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}

export function BoardGroup({
  eyebrow,
  title,
  subtitle,
  total,
  done,
  blocking = 0,
  accent = "var(--primary)",
  open,
  onOpenChange,
  children,
}: BoardGroupProps) {
  const toggle = () => onOpenChange(!open);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    // No `overflow-hidden` here: it would make this section its own scroll
    // container and stop the header below from sticking. The rounding it used
    // to clip is applied to the header and the body instead.
    <section className="glow-card-static rounded-xl">
      <header
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
          }
        }}
        // Sticky under PipelineRibbon (h-12, sticky top-0 z-30), so the group a
        // long task list belongs to stays named while scrolling through it.
        // Collapsed, the header is the whole card, so it has to round both ends
        // — the section has no `overflow-hidden` to clip it against the ring.
        className={`bg-card sticky top-12 z-10 flex cursor-pointer items-center gap-3 rounded-t-xl px-4 py-3 ${open ? "" : "rounded-b-xl"}`}
        style={{ boxShadow: `inset 3px 0 0 ${accent}` }}
      >
        <ChevronRight
          className={`text-muted-foreground size-4 shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="eyebrow" style={{ color: accent }}>
            {eyebrow}
          </span>
          <div className="flex items-baseline gap-2">
            <h2 className="text-foreground truncate text-[15px] font-semibold tracking-tight">
              {title}
            </h2>
            {subtitle ? (
              <span className="text-muted-foreground hidden truncate text-[12px] md:inline">
                — {subtitle}
              </span>
            ) : null}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-4">
          {blocking > 0 ? (
            <span className="font-data text-[10px] tracking-[0.12em] text-[var(--rag-red)] uppercase">
              {blocking} critical
            </span>
          ) : null}
          <div className="flex items-center gap-2">
            <div className="bg-muted hidden h-1.5 w-24 overflow-hidden rounded-full sm:block">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${pct}%`, background: accent }}
              />
            </div>
            <span className="font-data text-foreground text-[12px] font-semibold tabular-nums">
              {done}
              <span className="text-muted-foreground">/{total}</span>
            </span>
          </div>
        </div>
      </header>

      {open ? (
        <div className="border-border overflow-hidden rounded-b-xl border-t">{children}</div>
      ) : null}
    </section>
  );
}
